import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../hooks/useExcelExport';
import { usePDFExport } from '../../hooks/usePDFExport';
import { PAGINATION_CONFIG } from '../../constants/statisticsConstants';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';
import { formatCurrency } from '../../utils/statisticsUtils';

const fmt2 = (val) => (val != null ? Number(val).toFixed(2) : '-');
const fmt0 = (val) => (val != null ? Number(val).toFixed(0) : '-');
const fmtCurrency = (val, formatFn) => (val != null ? formatFn(Number(val).toFixed(0)) : '-');

const DebtBalancesModal = ({ open, handleClose, header, results, cuotaPura }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);
  const [isPdfEditOpen, setIsPdfEditOpen] = useState(false);
  const [editableRows, setEditableRows] = useState([]);

  const { exportToExcel } = useExcelExport();
  const { exportBlockFormatToPDF } = usePDFExport();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = () => {
    const data = results.map((result) => ({
      Grupo: result.group,
      'Número de Contrato': result.order_number,
      Suscriptor: result.subscriber_name,
      'Nombre del Plan': result.plan_name,
      'Método de Pago': result.payment_method,
      Estado: result.status,
      'Cuotas Pagadas': result.paid_installments != null ? parseFloat(fmt2(result.paid_installments)) : '-',
      '% Dif. Simple': result.simple_adjustment_percentage != null ? parseFloat(fmt2(result.simple_adjustment_percentage)) : '-',
      '% Dif. Doble': result.double_adjustment_percentage != null ? parseFloat(fmt2(result.double_adjustment_percentage)) : '-',
      '% Dif. Adecuación Simple': result.simple_adequation_percentage != null ? parseFloat(fmt2(result.simple_adequation_percentage)) : '-',
      '% Dif. Adecuación Doble': result.double_adequation_percentage != null ? parseFloat(fmt2(result.double_adequation_percentage)) : '-',
      'Cuotas Dif.': result.total_differed_amount != null ? parseFloat(fmt2(result.total_differed_amount)) : '-',
      'Cuotas Dif. Pagadas': parseFloat(result.paid_differment_installments?.toFixed(2) || 0),
      'Cuotas Pagadas Mensual': result.monthly_paid_installments != null ? parseFloat(fmt2(result.monthly_paid_installments)) : '-',
      'Cuotas Puntaje': result.score_installments != null ? parseFloat(fmt2(result.score_installments)) : '-',
      'Cuotas Adj. Pagadas': result.adjudicated_installments != null ? parseFloat(fmt2(result.adjudicated_installments)) : '-',
      'Total Cuotas Pagadas': result.total_paid_installments != null ? parseFloat(fmt2(result.total_paid_installments)) : '-',
      'Fecha Último Pago': result.last_payment_date,
      'Cuotas Vencidas': result.overdue_installments != null ? parseFloat(fmt2(result.overdue_installments)) : '-',
      'Deuda Mora Mensual': result.monthly_due_mora != null ? parseFloat(fmt2(result.monthly_due_mora)) : '-',
      'Cuotas a Vencer Mensual': result.monthly_due_installments != null ? parseFloat(fmt2(result.monthly_due_installments)) : '-',
      'Cuotas Adj. Restantes': result.remaining_adjudicated_installments != null ? parseFloat(fmt2(result.remaining_adjudicated_installments)) : '-',
      'Cuotas Adeudadas': result.due_installments != null ? parseFloat(fmt2(result.due_installments)) : '-',
      'Fecha Mora Más Antigua': result.oldest_due_date,
      'Monto Pagado Histórico': result.historical_paid_amount != null ? parseFloat(fmt0(result.historical_paid_amount)) : '-',
      'Deuda Mora': result.mora_debt != null ? parseFloat(fmt0(result.mora_debt)) : '-',
      'Deuda Próxima': result.upcoming_debt != null ? parseFloat(fmt0(result.upcoming_debt)) : '-',
      'Deuda Total a Cancelar': result.total_debt_to_cancel != null ? parseFloat(fmt0(result.total_debt_to_cancel)) : '-',
      'Fecha Acuerdo Aniversario': result.anniversary_agreement_date || '-',
      'Aniv. Cuotas Pagadas': result.anniversary_paid_installments ?? 0,
      'Aniv. Cuotas Pendientes': result.anniversary_pending_installments ?? 0,
      'Aniv. Saldo Pendiente': result.anniversary_pending_amount != null ? parseFloat(fmt0(result.anniversary_pending_amount)) : 0,
      'Aniv. Cuotas Vencidas': result.anniversary_overdue_installments ?? 0,
      'Aniv. Deuda Mora': result.anniversary_overdue_debt != null ? parseFloat(fmt0(result.anniversary_overdue_debt)) : 0,
      'Deuda Combinada a Cancelar': result.combined_debt_to_cancel != null ? parseFloat(fmt0(result.combined_debt_to_cancel)) : '-',
      'Aniv. Falta Fecha Acuerdo': result.anniversary_missing_agreement_date ? 'SI' : 'NO',
    }));

    exportToExcel(data, 'SaldosDeDeuda');
  };

  const normalizedCuotaPura =
    cuotaPura !== undefined && cuotaPura !== null && `${cuotaPura}`.trim() !== ''
      ? Number(`${cuotaPura}`.replace(',', '.'))
      : null;

  const buildEditableRows = () =>
    results.map((result) => ({
      valor_cuota: normalizedCuotaPura !== null ? formatCurrency(normalizedCuotaPura) : '-',
      subscriber_name: result.subscriber_name,
      diferimiento: [
        result.simple_adjustment_percentage,
        result.double_adjustment_percentage,
        result.simple_adequation_percentage,
        result.double_adequation_percentage,
      ].every((v) => v != null)
        ? `${(
            (result.simple_adjustment_percentage ?? 0) +
            (result.double_adjustment_percentage ?? 0) +
            (result.simple_adequation_percentage ?? 0) +
            (result.double_adequation_percentage ?? 0)
          ).toFixed(2)}%`
        : '-',
      cuotas_vencidas: fmt2(result.overdue_installments),
      deuda_mora: fmtCurrency(result.mora_debt, formatCurrency),
      deuda_diferimiento: fmtCurrency(result.diferiment_debt, formatCurrency),
      total_cuotas_pagadas: fmt2(result.total_paid_installments),
      cuotas_diferimiento_pagadas: result.paid_differment_installments?.toFixed(2) || '0.00',
      cuotas_adeudadas: fmt2(result.due_installments),
      deuda_total_cancelar: fmtCurrency(result.total_debt_to_cancel, formatCurrency),
      anniversary_agreement_date: result.anniversary_agreement_date || '-',
      anniversary_pending_installments: fmt2(result.anniversary_pending_installments ?? 0),
      anniversary_pending_amount: fmtCurrency(result.anniversary_pending_amount ?? 0, formatCurrency),
      anniversary_overdue_installments: fmt2(result.anniversary_overdue_installments ?? 0),
      anniversary_overdue_debt: fmtCurrency(result.anniversary_overdue_debt ?? 0, formatCurrency),
      combined_debt_to_cancel: fmtCurrency(result.combined_debt_to_cancel, formatCurrency),
      anniversary_missing_agreement_date: result.anniversary_missing_agreement_date ? 'SI' : 'NO',
      plan_name: result.plan_name,
      order_number: result.order_number,
      group: result.group,
    }));

  const handleExportToPDF = () => {
    setEditableRows(buildEditableRows());
    setIsPdfEditOpen(true);
  };

  const handlePdfFieldChange = (index, field, value) => {
    setEditableRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const confirmPdfExport = () => {
    const headers = [
      'Valor cuota',
      'Suscriptor',
      'Diferimiento',
      'Cuotas Vencidas',
      'Deuda Mora',
      'Deuda Diferimiento',
      'Total Cuotas Pagadas',
      'Cuotas Diferimiento Pagadas',
      'Cuotas Adeudadas',
      'Deuda Total a Cancelar',
      'Fecha Acuerdo Aniv.',
      'Aniv. Cuotas Pend.',
      'Aniv. Saldo Pend.',
      'Aniv. Cuotas Venc.',
      'Aniv. Deuda Mora',
      'Deuda Combinada',
      'Falta Acuerdo Aniv.',
    ];
    const data = editableRows.map((row) => [
      row.valor_cuota,
      row.subscriber_name,
      row.diferimiento,
      row.cuotas_vencidas,
      row.deuda_mora,
      row.deuda_diferimiento,
      row.total_cuotas_pagadas,
      row.cuotas_diferimiento_pagadas,
      row.cuotas_adeudadas,
      row.deuda_total_cancelar,
      row.anniversary_agreement_date,
      row.anniversary_pending_installments,
      row.anniversary_pending_amount,
      row.anniversary_overdue_installments,
      row.anniversary_overdue_debt,
      row.combined_debt_to_cancel,
      row.anniversary_missing_agreement_date,
    ]);

    const printDate = new Date().toISOString().split('T')[0];

    exportBlockFormatToPDF({
      title: 'Saldos de Deuda',
      headers,
      data,
      filename: `${
        results.length === 1 ? `${results[0].order_number}-` : ''
      }saldos_de_deuda_${printDate}`,
      columnsPerRow: 3,
      rightTopText: `Fecha impresión: ${printDate}`,
      recordTitleResolver: (_, index) => {
        const result = editableRows[index] || {};
        const planName = result.plan_name || 'Plan';
        const contractNumber = result.order_number || '';
        const group = result.group || '';
        const parts = [];
        if (planName) parts.push(planName);
        if (contractNumber) parts.push(`Contrato ${contractNumber}`);
        if (group) parts.push(`Grupo ${group}`);
        return parts.join(' - ') || `Registro ${index + 1}`;
      },
    });

    setIsPdfEditOpen(false);
  };

  if (!header || !results || results.length === 0) {
    return null;
  }

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const canExportPdf = results.length === 1 && !!results[0]?.order_number;

  return (
    <>
      <DialogContainer open={open} onClose={handleClose} size="fullWidth">
        <StatisticsDialogTitle
          title="Saldos de deuda"
          onExportExcel={handleExportToExcel}
          onExportPDF={canExportPdf ? handleExportToPDF : undefined}
          showPdfButton={canExportPdf}
          onClose={handleClose}
        />
        <DialogBody>
          <Box
            sx={{ mb: 3, textAlign: 'center', border: '1px solid #4a90e2', p: 2, borderRadius: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
              {header.constructora_info}
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
              Actualizado a la fecha: {header.updated_at_date}
            </Typography>
            <Typography variant="body2" sx={{ color: '#555' }}>
              {header.date_filter_text}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: '#555' }}>
            Cantidad de contratos encontrados: {results.length}
          </Typography>
          {!canExportPdf && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
              La descarga de PDF solo está disponible cuando se filtra por un número de contrato.
            </Typography>
          )}

          <GeneralTableBox>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <HeadTableCell>Grupo</HeadTableCell>
                  <HeadTableCell>Número de Contrato</HeadTableCell>
                  <HeadTableCell>Suscriptor</HeadTableCell>
                  <HeadTableCell>Nombre del Plan</HeadTableCell>
                  <HeadTableCell>Método de Pago</HeadTableCell>
                  <HeadTableCell>Estado</HeadTableCell>
                  <HeadTableCell>Cuotas Pagadas</HeadTableCell>
                  <HeadTableCell>% Dif. Simple</HeadTableCell>
                  <HeadTableCell>% Dif. Doble</HeadTableCell>
                  <HeadTableCell>% Dif. Adec. Simple</HeadTableCell>
                  <HeadTableCell>% Dif. Adec. Doble</HeadTableCell>
                  <HeadTableCell>Cuotas Dif.</HeadTableCell>
                  <HeadTableCell>Cuotas Dif. Pagadas</HeadTableCell>
                  <HeadTableCell>Cuotas Pagadas Mensual</HeadTableCell>
                  <HeadTableCell>Cuotas Puntaje</HeadTableCell>
                  <HeadTableCell>Cuotas Adj. Pagadas</HeadTableCell>
                  <HeadTableCell>Total Cuotas Pagadas</HeadTableCell>
                  <HeadTableCell>Fecha Último Pago</HeadTableCell>
                  <HeadTableCell>Cuotas Vencidas</HeadTableCell>
                  <HeadTableCell>Deuda Mora Mensual</HeadTableCell>
                  <HeadTableCell>Cuotas a Vencer Mensual</HeadTableCell>
                  <HeadTableCell>Cuotas Adj. Restantes</HeadTableCell>
                  <HeadTableCell>Cuotas Adeudadas</HeadTableCell>
                  <HeadTableCell>Fecha Mora Más Antigua</HeadTableCell>
                  <HeadTableCell>Monto Pagado Histórico</HeadTableCell>
                  <HeadTableCell>Deuda Mora</HeadTableCell>
                  <HeadTableCell>Deuda Diferimento</HeadTableCell>
                  <HeadTableCell>Deuda Próxima</HeadTableCell>
                  <HeadTableCell>Deuda Total a Cancelar</HeadTableCell>
                  <HeadTableCell>Fecha Acuerdo Aniv.</HeadTableCell>
                  <HeadTableCell>Aniv. Cuotas Pagadas</HeadTableCell>
                  <HeadTableCell>Aniv. Cuotas Pend.</HeadTableCell>
                  <HeadTableCell>Aniv. Saldo Pend.</HeadTableCell>
                  <HeadTableCell>Aniv. Cuotas Venc.</HeadTableCell>
                  <HeadTableCell>Aniv. Deuda Mora</HeadTableCell>
                  <HeadTableCell>Deuda Combinada</HeadTableCell>
                  <HeadTableCell>Obs. Aniv.</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedResults.map((result, index) => (
                  <TableRow key={index}>
                    <RowTableCell>{result.group}</RowTableCell>
                    <RowTableCell>{result.order_number}</RowTableCell>
                    <RowTableCell>{result.subscriber_name}</RowTableCell>
                    <RowTableCell>{result.plan_name}</RowTableCell>
                    <RowTableCell>{result.payment_method}</RowTableCell>
                    <RowTableCell>{result.status}</RowTableCell>
                    <RowTableCell>{fmt2(result.paid_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.simple_adjustment_percentage)}</RowTableCell>
                    <RowTableCell>{fmt2(result.double_adjustment_percentage)}</RowTableCell>
                    <RowTableCell>{fmt2(result.simple_adequation_percentage)}</RowTableCell>
                    <RowTableCell>{fmt2(result.double_adequation_percentage)}</RowTableCell>
                    <RowTableCell>{fmt2(result.total_differed_amount)}</RowTableCell>
                    <RowTableCell>{result.paid_differment_installments?.toFixed(2) || '0.00'}</RowTableCell>
                    <RowTableCell>{fmt2(result.monthly_paid_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.score_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.adjudicated_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.total_paid_installments)}</RowTableCell>
                    <RowTableCell>{result.last_payment_date}</RowTableCell>
                    <RowTableCell>{fmt2(result.overdue_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.monthly_due_mora)}</RowTableCell>
                    <RowTableCell>{fmt2(result.monthly_due_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.remaining_adjudicated_installments)}</RowTableCell>
                    <RowTableCell>{fmt2(result.due_installments)}</RowTableCell>
                    <RowTableCell>{result.oldest_due_date}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.historical_paid_amount, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.mora_debt, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.diferiment_debt, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.upcoming_debt, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.total_debt_to_cancel, formatCurrency)}</RowTableCell>
                    <RowTableCell>{result.anniversary_agreement_date || '-'}</RowTableCell>
                    <RowTableCell>{fmt2(result.anniversary_paid_installments ?? 0)}</RowTableCell>
                    <RowTableCell>{fmt2(result.anniversary_pending_installments ?? 0)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.anniversary_pending_amount ?? 0, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmt2(result.anniversary_overdue_installments ?? 0)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.anniversary_overdue_debt ?? 0, formatCurrency)}</RowTableCell>
                    <RowTableCell>{fmtCurrency(result.combined_debt_to_cancel, formatCurrency)}</RowTableCell>
                    <RowTableCell>{result.anniversary_missing_agreement_date ? 'Falta fecha acuerdo' : '-'}</RowTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GeneralTableBox>

          <TablePagination
            component="div"
            count={results.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Contratos por página"
            rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
          />
        </DialogBody>
      </DialogContainer>

      <Dialog open={isPdfEditOpen} onClose={() => setIsPdfEditOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Editar campos antes de exportar PDF</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 600 }}>
          {editableRows.map((row, index) => (
            <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {row.plan_name || 'Plan'} - Contrato {row.order_number} - Grupo {row.group}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 2,
                }}
              >
                <TextField
                  label="Valor cuota"
                  value={row.valor_cuota}
                  onChange={(e) => handlePdfFieldChange(index, 'valor_cuota', e.target.value)}
                />
                <TextField
                  label="Suscriptor"
                  value={row.subscriber_name}
                  onChange={(e) => handlePdfFieldChange(index, 'subscriber_name', e.target.value)}
                />
                <TextField
                  label="Diferimiento"
                  value={row.diferimiento}
                  onChange={(e) => handlePdfFieldChange(index, 'diferimiento', e.target.value)}
                />
                <TextField
                  label="Cuotas Vencidas"
                  value={row.cuotas_vencidas}
                  onChange={(e) => handlePdfFieldChange(index, 'cuotas_vencidas', e.target.value)}
                />
                <TextField
                  label="Deuda Mora"
                  value={row.deuda_mora}
                  onChange={(e) => handlePdfFieldChange(index, 'deuda_mora', e.target.value)}
                />
                <TextField
                  label="Deuda Diferimiento"
                  value={row.deuda_diferimiento}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'deuda_diferimiento', e.target.value)
                  }
                />
                <TextField
                  label="Total Cuotas Pagadas"
                  value={row.total_cuotas_pagadas}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'total_cuotas_pagadas', e.target.value)
                  }
                />
                <TextField
                  label="Cuotas Diferimiento Pagadas"
                  value={row.cuotas_diferimiento_pagadas}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'cuotas_diferimiento_pagadas', e.target.value)
                  }
                />
                <TextField
                  label="Cuotas Adeudadas"
                  value={row.cuotas_adeudadas}
                  onChange={(e) => handlePdfFieldChange(index, 'cuotas_adeudadas', e.target.value)}
                />
                <TextField
                  label="Deuda Total a Cancelar"
                  value={row.deuda_total_cancelar}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'deuda_total_cancelar', e.target.value)
                  }
                />
                <TextField
                  label="Fecha Acuerdo Aniv."
                  value={row.anniversary_agreement_date}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'anniversary_agreement_date', e.target.value)
                  }
                />
                <TextField
                  label="Aniv. Cuotas Pend."
                  value={row.anniversary_pending_installments}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'anniversary_pending_installments', e.target.value)
                  }
                />
                <TextField
                  label="Aniv. Saldo Pend."
                  value={row.anniversary_pending_amount}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'anniversary_pending_amount', e.target.value)
                  }
                />
                <TextField
                  label="Aniv. Cuotas Venc."
                  value={row.anniversary_overdue_installments}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'anniversary_overdue_installments', e.target.value)
                  }
                />
                <TextField
                  label="Aniv. Deuda Mora"
                  value={row.anniversary_overdue_debt}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'anniversary_overdue_debt', e.target.value)
                  }
                />
                <TextField
                  label="Deuda Combinada"
                  value={row.combined_debt_to_cancel}
                  onChange={(e) =>
                    handlePdfFieldChange(index, 'combined_debt_to_cancel', e.target.value)
                  }
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPdfEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmPdfExport}>
            Descargar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DebtBalancesModal;
